import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  type BulkCreateStakeholderRelationsResult,
  type StakeholderSetting,
  useStakeholderRelationMutations,
} from "../../../hooks";
import {
  collectEmails,
  parseStakeholderRelations,
} from "../parseStakeholderRelations";
import { RESPONDENT_TYPE_LABELS } from "../stakeholderSetting";

// 関係性1件につき、設問シートと要約シートを1枚ずつ複製する。
// 登録済みの関係性は入稿時に読み飛ばされるため、実際の複製枚数はこれより少なくなりうる
const SHEETS_PER_RELATION = 2;

// 一覧で全件出すと大量入稿でダイアログが伸びきるため、確認に足る件数までにする
const _MAX_LISTED_EMAILS = 20;

interface StakeholderRelationImportDialogProps {
  open: boolean;
  termId: string;
  settings: readonly StakeholderSetting[];
  onClose: () => void;
  onImported: (result: BulkCreateStakeholderRelationsResult) => void;
}

export function StakeholderRelationImportDialog({
  open,
  termId,
  settings,
  onClose,
  onImported,
}: StakeholderRelationImportDialogProps) {
  const {
    findUnregisteredEmails,
    bulkCreateStakeholderRelations,
    loading,
    error,
    previewError,
  } = useStakeholderRelationMutations();

  const [settingId, setSettingId] = useState("");
  const [input, setInput] = useState("");
  const [unregisteredEmails, setUnregisteredEmails] = useState<string[] | null>(
    null,
  );
  // 入力が続くと問い合わせが複数走るため、最後に投げたものの結果だけを採用する
  const previewRequestIdRef = useRef(0);

  useEffect(() => {
    if (!open) return;
    setInput("");
    setUnregisteredEmails(null);
  }, [open]);

  useEffect(() => {
    setSettingId((current) =>
      settings.some((setting) => setting.stakeholderSettingId === current)
        ? current
        : (settings[0]?.stakeholderSettingId ?? ""),
    );
  }, [settings]);

  const parsed = useMemo(() => parseStakeholderRelations(input), [input]);
  // 入力のたびに配列を作り直すと件数プレビューの問い合わせが毎打鍵で走るため、文字列で同一性を見る
  const emailsKey = useMemo(
    () => collectEmails(parsed.rows).join("\n"),
    [parsed.rows],
  );

  // 貼り付けた時点で新しく登録される従業員を出しておき、登録前に気づけるようにする
  const loadUnregisteredEmails = useCallback(async () => {
    if (emailsKey.length === 0) {
      setUnregisteredEmails(null);
      return;
    }
    const requestId = previewRequestIdRef.current + 1;
    previewRequestIdRef.current = requestId;
    const emails = await findUnregisteredEmails(termId, emailsKey.split("\n"));
    if (requestId === previewRequestIdRef.current)
      setUnregisteredEmails(emails);
  }, [emailsKey, termId, findUnregisteredEmails]);

  useEffect(() => {
    if (!open) return;
    loadUnregisteredEmails();
  }, [open, loadUnregisteredEmails]);

  if (!open) return null;

  // 従業員の作成は取り消せないため、何人増えるか確認できていない状態では入稿させない
  const canSubmit =
    settingId.length > 0 &&
    parsed.rows.length > 0 &&
    parsed.errors.length === 0 &&
    unregisteredEmails !== null &&
    !loading;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    const result = await bulkCreateStakeholderRelations(settingId, parsed.rows);
    if (result) {
      onImported(result);
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50"
      onClick={() => {
        if (!loading) onClose();
      }}
    >
      <div
        className="max-h-[90vh] w-[90%] max-w-[720px] overflow-auto rounded-xl bg-white p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="mb-1 font-semibold text-[var(--color-text-primary)] text-lg">
          関係性の一括入稿
        </h3>
        <p className="mt-0 mb-5 text-[13px] text-[var(--color-text-secondary)]">
          誰が誰について語るかを登録します。未登録の従業員はこの時点で作成され、あとから取り消せません
        </p>
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label
              htmlFor="relationSettingId"
              className="mb-1.5 block font-medium text-[var(--color-text-primary)] text-sm"
            >
              対象のインタビュー設定
            </label>
            <select
              id="relationSettingId"
              value={settingId}
              onChange={(e) => setSettingId(e.target.value)}
              className="box-border w-full rounded-md border border-[var(--color-border-default)] px-3 py-2.5 text-sm outline-none"
            >
              {settings.map((setting) => (
                <option
                  key={setting.stakeholderSettingId}
                  value={setting.stakeholderSettingId}
                >
                  {setting.name}（
                  {RESPONDENT_TYPE_LABELS[setting.respondentType]}）
                </option>
              ))}
            </select>
          </div>
          <div className="mb-4">
            <label
              htmlFor="relationInput"
              className="mb-1.5 block font-medium text-[var(--color-text-primary)] text-sm"
            >
              入稿データ
            </label>
            <textarea
              id="relationInput"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              rows={10}
              placeholder={
                "member@example.co.jp\t田中 優子\tboss@example.co.jp\t鈴木 健一"
              }
              className="box-border w-full resize-y rounded-md border border-[var(--color-border-default)] px-3 py-2.5 font-mono text-[13px] outline-none"
            />
            <p className="mt-1 mb-0 text-[13px] text-[var(--color-text-secondary)]">
              レポートを受け取る人（インタビューで語られる本人）のメール・氏名、回答者（語る人）のメール・氏名
              の4列。スプレッドシートからの貼り付け（タブ区切り）とCSV（カンマ区切り）のどちらでも入稿できます
            </p>
          </div>

          {parsed.rows.length > 0 && (
            <div className="mb-4 rounded-md bg-[var(--color-bg-subtle-gray)] p-4 text-sm">
              <p className="m-0 font-medium text-[var(--color-text-primary)]">
                {parsed.rows.length}件の関係性を読み取りました。
              </p>
              <p className="mt-1 mb-0 text-[13px] text-[var(--color-text-secondary)]">
                {/* 登録済みの組み合わせは複製されないため、実際の枚数はこれより少なくなりうる */}
                複製されるシートは最大
                {parsed.rows.length * SHEETS_PER_RELATION}枚です
              </p>
              {parsed.duplicateCount > 0 && (
                <p className="mt-1 mb-0 text-[13px] text-[var(--color-text-secondary)]">
                  同じ組み合わせだった{parsed.duplicateCount}行は除外しました
                </p>
              )}
              {unregisteredEmails !== null &&
                unregisteredEmails.length === 0 && (
                  <p className="mt-1 mb-0 text-[13px] text-[var(--color-text-secondary)]">
                    入稿データのメールアドレスはすべて登録済みの従業員です（新しく作成される従業員はいません）
                  </p>
                )}
              {parsed.conflictingNameCount > 0 && (
                <p className="mt-1 mb-0 text-[13px] text-[var(--color-text-secondary)]">
                  同じメールアドレスに複数の氏名が指定されている人が
                  {parsed.conflictingNameCount}
                  名います（先に現れた氏名で登録します）
                </p>
              )}
            </div>
          )}

          {unregisteredEmails !== null && unregisteredEmails.length > 0 && (
            <div className="mb-4 rounded-md bg-[#fffbeb] p-4 text-sm">
              <p className="m-0 mb-1 font-medium text-[#b45309]">
                この入稿で従業員{unregisteredEmails.length}
                名が新しく作成されます（あとから取り消せません）
              </p>
              <ul className="m-0 list-disc pl-5 text-[13px] text-[var(--color-text-secondary)]">
                {unregisteredEmails
                  .slice(0, _MAX_LISTED_EMAILS)
                  .map((email) => (
                    <li key={email}>{email}</li>
                  ))}
              </ul>
              {unregisteredEmails.length > _MAX_LISTED_EMAILS && (
                <p className="mt-1 mb-0 text-[13px] text-[var(--color-text-secondary)]">
                  ほか{unregisteredEmails.length - _MAX_LISTED_EMAILS}名
                </p>
              )}
            </div>
          )}

          {parsed.errors.length > 0 && (
            <div className="mb-4 rounded-md bg-[#fef2f2] p-4 text-sm">
              <p className="m-0 mb-1 font-medium text-[#ef4444]">
                入稿できない行があります
              </p>
              <ul className="m-0 list-disc pl-5 text-[#ef4444] text-[13px]">
                {parsed.errors.map((message) => (
                  <li key={message}>{message}</li>
                ))}
              </ul>
            </div>
          )}

          {previewError && unregisteredEmails === null && (
            <div className="mb-4 rounded-md bg-[#fffbeb] p-4 text-sm">
              <p className="m-0 font-medium text-[#b45309]">
                新しく登録される従業員数を確認できませんでした（{previewError}
                ）。確認できるまで入稿できません
              </p>
              <button
                type="button"
                onClick={() => loadUnregisteredEmails()}
                className="mt-2 cursor-pointer rounded-md border border-[#b45309] bg-white px-3 py-1.5 font-medium text-[#b45309] text-[13px]"
              >
                再確認する
              </button>
            </div>
          )}

          {error && (
            <div className="mb-4 rounded-md bg-[#fef2f2] p-4 font-medium text-[#ef4444] text-sm">
              入稿に失敗しました（{error}）
            </div>
          )}

          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className={`rounded-md border border-[var(--color-text-secondary)] bg-white px-4 py-2.5 font-medium text-[var(--color-text-secondary)] text-sm ${loading ? "cursor-not-allowed" : "cursor-pointer"}`}
            >
              キャンセル
            </button>
            <button
              type="submit"
              disabled={!canSubmit}
              className={`rounded-md border-none px-4 py-2.5 font-medium text-sm text-white ${
                canSubmit
                  ? "cursor-pointer bg-[var(--color-primary-500)]"
                  : "cursor-not-allowed bg-[#9ca3af]"
              }`}
            >
              {loading ? "入稿中..." : "入稿する"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
